import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GenericModal } from "../../../Components";
import { IMAGES } from "../../../Assets";
import { saveMessageApi } from "../../../Redux/api/chat";
import { getRealtimeStagesApi } from "../../../Redux/api/realtime";
import useRealtimeSession from "../../../hooks/useRealtimeSession";
import { HumanBody } from "../HumanBody";
import { MusclesStructure } from "../MusclesStructure";
import {
  femaleFullbodyParts,
  maleFullbodyParts,
} from "../components/HumanBodyPartsList";
import { musclesbodyParts } from "../components/MusclesBodyPartsList";
import AvatarOrb from "./AvatarOrb";
import StageRail from "./StageRail";
import "./realtimeVoice.css";

const FALLBACK_STAGES_EN = [
  { id: 1, name: "Chief Complaint" },
  { id: 2, name: "Pain Inquiry" },
  { id: 3, name: "Complaint Duration" },
  { id: 4, name: "Associated Concerns" },
  { id: 5, name: "Patient History" },
  { id: 6, name: "Family History" },
  { id: 7, name: "Additional Information" },
  { id: 8, name: "Closure" },
];

const FALLBACK_STAGES_AR = [
  { id: 1, name: "الشكوى الرئيسية" },
  { id: 2, name: "استفسار الألم" },
  { id: 3, name: "مدة الشكوى" },
  { id: 4, name: "المخاوف المرتبطة" },
  { id: 5, name: "التاريخ المرضي" },
  { id: 6, name: "التاريخ العائلي" },
  { id: 7, name: "معلومات إضافية" },
  { id: 8, name: "الخِتام" },
];

/**
 * Realtime voice-to-voice pre-consultant UI.
 */
export default function RealtimeVoice({
  state,
  chatId,
  medUser,
  doctorId,
  handleEndSession,
  onActiveChange,
}) {
  const language = state?.language || "en";
  const isArabic = language === "ar";
  const avatarId = state?.avatar?.id ?? 0;
  const avatarImage =
    state?.avatar?.image ||
    (avatarId === 1
      ? IMAGES.FEMALE_DOCTOR_AVATAR
      : IMAGES.MALE_DOCTOR_AVATAR);

  const [activeStage, setActiveStage] = useState(1);
  const [routingStage, setRoutingStage] = useState(null);
  const [localStages, setLocalStages] = useState(
    isArabic ? FALLBACK_STAGES_AR : FALLBACK_STAGES_EN
  );
  const [transcript, setTranscript] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [anatomyModal, setAnatomyModal] = useState(false);
  const [selectTab, setSelectTab] = useState(1);
  const [avatarData, setAvatarData] = useState([]);
  const [lastAnatomyMessage, setLastAnatomyMessage] = useState("");
  const [ending, setEnding] = useState(false);

  const turnCountRef = useRef(0);
  const persistQueueRef = useRef(Promise.resolve());
  const endingRef = useRef(false);
  const routingTimerRef = useRef(null);

  const sessionPayload = useMemo(() => {
    const specializationMap = {
      1: "physician",
      2: "gastroenterologist",
      3: "pulmonologist",
      4: "urologist",
      5: "orthopedic",
    };
    const raw = state?.specialization;
    const doctorField =
      specializationMap[raw] ||
      specializationMap[parseInt(raw, 10)] ||
      raw ||
      "physician";

    return {
      required_language: language,
      doctorField,
      Id: avatarId,
      name: medUser?.name,
      age: medUser?.age,
      gender: medUser?.gender,
      occupation: medUser?.occupation,
    };
  }, [language, state?.specialization, avatarId, medUser]);

  const persistTurn = useCallback(
    (role, text) => {
      const trimmed = String(text || "").trim();
      if (!trimmed || !chatId || !medUser?.userId || !doctorId) return;

      const isFirst = turnCountRef.current === 0;
      turnCountRef.current += 1;

      const payload = {
        fromUserId: role === "user" ? medUser.userId : doctorId,
        toUserId: role === "user" ? doctorId : medUser.userId,
        chatId,
        isFile: false,
        description: trimmed,
        isFirstMessage: isFirst,
        language,
      };

      persistQueueRef.current = persistQueueRef.current
        .then(() => saveMessageApi(payload))
        .catch((err) => {
          console.warn("Failed to persist voice turn:", err);
        });
    },
    [chatId, doctorId, language, medUser?.userId]
  );

  const handleTurn = useCallback(
    (role, text) => {
      const trimmed = String(text || "").trim();
      if (!trimmed) return;
      setTranscript((prev) => [
        ...prev,
        { id: `${Date.now()}-${Math.random()}`, role, text: trimmed },
      ]);
      persistTurn(role, trimmed);
    },
    [persistTurn]
  );

  const handleStageChange = useCallback(({ stage }) => {
    const next = Number(stage);
    if (!Number.isFinite(next) || next < 1 || next > 8) return;

    setActiveStage((prev) => {
      // Guard against backwards transitions
      if (next < prev) return prev;
      setRoutingStage(next);
      if (routingTimerRef.current) clearTimeout(routingTimerRef.current);
      routingTimerRef.current = setTimeout(() => setRoutingStage(null), 1200);
      return next;
    });
  }, []);

  const handleOpenAnatomy = useCallback(({ view }) => {
    setSelectTab(view === "muscular_body" ? 2 : 1);
    setAnatomyModal(true);
  }, []);

  const {
    status,
    error,
    isUserSpeaking,
    isAssistantSpeaking,
    isMuted,
    assistantCaption,
    stages: sessionStages,
    physicianName,
    micAnalyserRef,
    remoteAnalyserRef,
    connect,
    disconnect,
    toggleMute,
    sendUserText,
  } = useRealtimeSession({
    sessionPayload,
    chatId,
    initialStage: activeStage,
    onTurn: handleTurn,
    onStageChange: handleStageChange,
    onOpenAnatomy: handleOpenAnatomy,
    onEndSession: async () => {
      if (endingRef.current) return;
      endingRef.current = true;
      setEnding(true);
      disconnect();
      try {
        await persistQueueRef.current;
      } catch (_) {
        /* ignore */
      }
      handleEndSession?.(chatId);
    },
  });

  useEffect(() => {
    onActiveChange?.(status === "connected" || status === "connecting" || status === "reconnecting");
  }, [status, onActiveChange]);

  // Prefetch stage labels
  useEffect(() => {
    let cancelled = false;
    getRealtimeStagesApi(language)
      .then((res) => {
        const data = res?.data?.data;
        if (!cancelled && Array.isArray(data) && data.length) {
          setLocalStages(data);
        }
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  useEffect(() => {
    if (Array.isArray(sessionStages) && sessionStages.length) {
      setLocalStages(sessionStages);
    }
  }, [sessionStages]);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    return () => {
      disconnect();
      if (routingTimerRef.current) clearTimeout(routingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Anatomy selection -> send text into realtime session
  useEffect(() => {
    if (!avatarData?.length) return;
    const messageKey = avatarData[0];
    let message = messageKey;

    if (selectTab === 1 && medUser?.gender !== "Female") {
      const part = maleFullbodyParts?.[messageKey] || {};
      message =
        language === "ar"
          ? part.arabic || messageKey
          : part.english || messageKey;
    } else if (selectTab === 2) {
      const part = musclesbodyParts?.[messageKey] || {};
      message =
        language === "ar"
          ? part.arabic || messageKey
          : part.english || messageKey;
    } else {
      const part = femaleFullbodyParts?.[messageKey] || {};
      message =
        language === "ar"
          ? part.arabic || messageKey
          : part.english || messageKey;
    }

    if (lastAnatomyMessage === message) return;
    setLastAnatomyMessage(message);
    setAvatarData([]);
    setAnatomyModal(false);

    const spoken =
      language === "ar"
        ? `يقوم المستخدم باختيار ${message} من البنية التشريحية التي تظهر على الشاشة.`
        : `The user selects ${message} from the anatomical structure on the screen.`;

    sendUserText(spoken);
  }, [
    avatarData,
    selectTab,
    medUser?.gender,
    language,
    lastAnatomyMessage,
    sendUserText,
  ]);

  const leftStages = localStages.filter((s) => s.id <= 4);
  const rightStages = localStages.filter((s) => s.id > 4);

  const statusLabel =
    status === "connected"
      ? isArabic
        ? "متصل"
        : "Listening"
      : status === "connecting"
      ? isArabic
        ? "جارٍ الاتصال"
        : "Connecting"
      : status === "reconnecting"
      ? isArabic
        ? "إعادة الاتصال"
        : "Reconnecting"
      : status === "error"
      ? isArabic
        ? "خطأ"
        : "Error"
      : status;

  const handleManualEnd = async () => {
    if (endingRef.current) return;
    endingRef.current = true;
    setEnding(true);
    disconnect();
    try {
      await persistQueueRef.current;
    } catch (_) {
      /* ignore */
    }
    handleEndSession?.(chatId);
  };

  return (
    <div className="realtime-voice-root" dir={isArabic ? "rtl" : "ltr"}>
      <div className="rv-header">
        <div className="rv-title">
          {isArabic ? "جلسة صوتية مباشرة" : "LIVE VOICE SESSION"}
        </div>
        <div className={`rv-status-pill ${status}`}>
          <span className="dot" />
          {statusLabel}
        </div>
      </div>

      <div className="rv-main">
        <StageRail
          stages={leftStages}
          activeStage={activeStage}
          routingStage={routingStage}
          side="left"
        />

        <AvatarOrb
          imageSrc={avatarImage}
          physicianName={
            physicianName ||
            (avatarId === 1
              ? isArabic
                ? "إليزابيث"
                : "Dr. Elizabeth"
              : isArabic
              ? "أحمد"
              : "Dr. Ahmad")
          }
          isUserSpeaking={isUserSpeaking}
          isAssistantSpeaking={isAssistantSpeaking}
          status={status}
          micAnalyserRef={micAnalyserRef}
          remoteAnalyserRef={remoteAnalyserRef}
          caption={assistantCaption}
        />

        <StageRail
          stages={rightStages}
          activeStage={activeStage}
          routingStage={routingStage}
          side="right"
        />
      </div>

      <div className="rv-controls">
        {error ? <div className="rv-error">{error}</div> : null}

        <div className="rv-controls-row">
          {status === "error" || status === "ended" ? (
            <button
              type="button"
              className="rv-btn rv-btn-primary"
              onClick={() => {
                endingRef.current = false;
                setEnding(false);
                connect();
              }}
            >
              {isArabic ? "إعادة الاتصال" : "Reconnect"}
            </button>
          ) : null}

          <button
            type="button"
            className="rv-btn rv-btn-ghost"
            onClick={toggleMute}
            disabled={status !== "connected"}
          >
            {isMuted
              ? isArabic
                ? "إلغاء كتم الصوت"
                : "Unmute Mic"
              : isArabic
              ? "كتم الميكروفون"
              : "Mute Mic"}
          </button>

          <button
            type="button"
            className="rv-btn rv-btn-danger"
            onClick={handleManualEnd}
            disabled={ending}
          >
            {ending
              ? isArabic
                ? "جارٍ الإنهاء…"
                : "Ending…"
              : isArabic
              ? "إنهاء الجلسة"
              : "End Session"}
          </button>
        </div>

        <div className="rv-transcript">
          <button
            type="button"
            className="rv-transcript-toggle"
            onClick={() => setShowTranscript((v) => !v)}
          >
            {showTranscript
              ? isArabic
                ? "إخفاء المحادثة"
                : "Hide transcript"
              : isArabic
              ? "إظهار المحادثة"
              : "Show transcript"}{" "}
            ({transcript.length})
          </button>
          {showTranscript ? (
            <div className="rv-transcript-body">
              {transcript.length === 0 ? (
                <div className="rv-turn">
                  {isArabic ? "لا توجد رسائل بعد." : "No turns yet."}
                </div>
              ) : (
                transcript.map((turn) => (
                  <div key={turn.id} className={`rv-turn ${turn.role}`}>
                    <strong>
                      {turn.role === "user"
                        ? isArabic
                          ? "أنت:"
                          : "You:"
                        : isArabic
                        ? "المستشار:"
                        : "Consultant:"}
                    </strong>
                    {turn.text}
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
      </div>

      {anatomyModal ? (
        <GenericModal
          size="lg"
          show={anatomyModal}
          onHide={() => setAnatomyModal(false)}
          closeModal
        >
          {selectTab === 1 ? (
            <HumanBody
              selectedPatient={medUser?.gender}
              avatarData={avatarData}
              setAvatarData={setAvatarData}
              anatomyModal={anatomyModal}
              onHide={() => setAnatomyModal(false)}
              bodyLoader={false}
            />
          ) : (
            <MusclesStructure
              selectedPatient={medUser?.gender}
              avatarData={avatarData}
              setAvatarData={setAvatarData}
              bodyLoader={false}
              anatomyModal={anatomyModal}
              onHide={() => setAnatomyModal(false)}
            />
          )}
          <div className="type-btn">
            <div
              className="anatomy-img cursor-pointer"
              onClick={() => setSelectTab(1)}
            >
              <img
                src={IMAGES.ANATOMY_FULL_BODY}
                alt="full body"
                className="img-fluid"
              />
            </div>
            <div
              className="anatomy-img cursor-pointer"
              onClick={() => setSelectTab(2)}
            >
              <img
                src={IMAGES.ANATOMY_MUSCLES_FRONT_IMAGE}
                alt="muscles"
                className="img-fluid"
              />
            </div>
          </div>
        </GenericModal>
      ) : null}
    </div>
  );
}
